import { Component, useEffect, useRef, useState } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { ApiError, UNAUTHORIZED, api, type Me } from './api'
import { invalidate } from './useApi'
import { Link, Redirect, Router, match, navigate, usePath } from './router'
import { Shell, type Crumb } from './components/Shell'
import { Login } from './pages/Login'
import { AuthPage, authKind } from './pages/Auth'
import { Koble } from './pages/Koble'
import { Godkjenn } from './pages/Godkjenn'
import { Enhet } from './pages/Enhet'
import { Enheter } from './pages/Enheter'
import { Account } from './pages/Account'
import { Operators, OperatorPage } from './pages/Operators'
import { Home } from './pages/Home'
import { Farm } from './pages/Farm'
import { Listen } from './pages/Listen'
import { SiteAppetitt } from './pages/SiteAppetitt'
import { SiteFeeding } from './pages/SiteFeeding'
import { SiteLaksvel } from './pages/SiteLaksvel'
import { SiteLoggbok } from './pages/SiteLoggbok'
import { SiteLoggbokSpor } from './pages/SiteLoggbokSpor'
import { SiteLoggbokUke } from './pages/SiteLoggbokUke'
import { SiteLog } from './pages/SiteLog'
import { Kart } from './pages/Kart'
import { Oppsett } from './pages/Oppsett'
import { Bokkode, Lusedagbok } from './pages/Lusedagbok'
import { Product } from './pages/Product'
import { Fleet } from './pages/Fleet'
import { Add } from './pages/Add'
import { ImpactPage } from './pages/Impact'
import { Sites, SitePage } from './pages/Sites'
import { Hydrophones, HydrophonePage } from './pages/Hydrophones'
import { MissionPage } from './pages/Mission'
import { NodePage } from './pages/Node'
import { Activity } from './pages/Activity'
import { SecurityDoc } from './pages/Docs'
import { Empty } from './components/bits'

export function App() {
  return (
    <Router>
      <Feilgrense>
        <Gate />
      </Feilgrense>
    </Router>
  )
}

/* WHAT A CRASH LOOKS LIKE, and why this is worth a class component in a
 * codebase that has none.
 *
 * React unmounts the entire tree when a render or an effect throws and nothing
 * catches it. With no boundary anywhere in this app, the result was a page that
 * painted correctly for a second and then went blank -- an empty body showing
 * the deep green behind it. No message, nothing in the UI, and the only way to
 * find out what happened was to open a console.
 *
 * That is the worst failure mode available: it looks like the product is
 * broken and gives nobody the one sentence that would say why. A person on a
 * boat cannot open devtools, and neither can somebody telling us on the phone
 * that "the page goes green".
 *
 * So: catch it, say what it was, and offer the two things that actually help --
 * reload, and go back to the front page. The message is shown verbatim rather
 * than replaced with an apology, because "Cannot read properties of undefined"
 * pasted into a message to us is worth more than any wording we could invent.
 */
interface FeilState { error: Error | null }

class Feilgrense extends Component<{ children: ReactNode }, FeilState> {
  state: FeilState = { error: null }

  static getDerivedStateFromError(error: Error): FeilState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // The component stack is the half that says WHERE, and React only ever
    // hands it to this method. Logged rather than rendered: it is forty lines
    // of minified names, useful in a console and noise on a screen.
    console.error('the page crashed', error, info.componentStack)
  }

  render() {
    if (!this.state.error) return this.props.children
    return (
      <div className="login-wrap">
        <div className="login stack" style={{ gap: 14 }}>
          <div className="wordmark">
            <span className="dot" aria-hidden="true" />
            Phoniq
          </div>
          <h1 style={{ margin: 0, fontSize: 18 }}>Noe gikk galt på denne siden</h1>
          <p className="muted" style={{ margin: 0, fontSize: 14 }}>
            Ingenting er tapt. Tellinger som er lagret ligger der de skal.
          </p>
          <pre className="feilmelding">{String(this.state.error?.message
            || this.state.error)}</pre>
          <div className="row" style={{ gap: 8 }}>
            <button className="btn primary"
              onClick={() => window.location.reload()}>Last siden på nytt</button>
            <button className="btn ghost"
              onClick={() => window.location.assign('/')}>Til forsiden</button>
          </div>
        </div>
      </div>
    )
  }
}

/** Session bootstrap.
 *
 *  The cookie is httpOnly, so JavaScript cannot see whether it exists. The only
 *  way to know if we are signed in is to ask, which is what GET /v1/me is for.
 *  A 401 here is the normal cold-start answer, not an error worth showing.
 */
function Gate() {
  const [me, setMe] = useState<Me | null>(null)
  const [checked, setChecked] = useState(false)
  const path = usePath()

  useEffect(() => {
    api
      .get<Me>('/v1/me')
      .then(setMe)
      .catch((e) => {
        if (!(e instanceof ApiError && e.status === 401)) {
          // A receiver that is down looks different from one that says no.
          console.error('could not reach the receiver', e)
        }
      })
      .finally(() => setChecked(true))
  }, [])

  // Any request, anywhere, can discover the session has expired. Rather than
  // teaching every caller, the shell listens once and drops back to the login
  // screen with the cache cleared.
  //
  // Except for a device, which has no login screen and no way to reach one.
  // When a manager unrolls a tablet it stops at its very next request, and the
  // ordinary path would leave whoever is holding it staring at a form asking
  // for an email address and a password that do not exist -- with no way out
  // of it but to know to type /koble into a browser bar, on a phone, in
  // gloves. The enrollment screen is where an unrolled device belongs, and
  // "ask your manager for a new code" is a thing the person standing there can
  // actually do.
  // A ref rather than reading `me` inside the updater: the listener is
  // registered once and must not close over a stale value, and a state updater
  // is not a place to navigate from -- StrictMode calls it twice.
  const wasDevice = useRef(false)
  wasDevice.current = me?.via === 'device'

  useEffect(() => {
    const onUnauth = () => {
      if (wasDevice.current) navigate('/koble')
      setMe(null)
      invalidate()
    }
    window.addEventListener(UNAUTHORIZED, onUnauth)
    return () => window.removeEventListener(UNAUTHORIZED, onUnauth)
  }, [])

  if (!checked) {
    return (
      <div className="login-wrap">
        <div className="login" aria-busy="true">
          <div className="wordmark">
            <span className="dot" aria-hidden="true" />
            Phoniq
          </div>
        </div>
      </div>
    )
  }

  /* Before the session check, not after. Somebody following a reset link has
     no session by definition, and the ordinary path would show them the login
     form they cannot get past. Signed-in visitors get these pages too -- a
     reset link clicked on a laptop that is already signed in is still a reset.
     On success the page navigates away, so a spent token is never re-rendered. */
  const kind = authKind(path)
  if (kind) {
    return (
      <AuthPage
        kind={kind}
        onSignedIn={(next) => {
          setMe(next)
          invalidate()
          navigate('/')
        }}
      />
    )
  }

  /* Also before the session check, and also WITH one if there is one.
   *
   * Before, because a device has no session by definition on its first visit
   * and would otherwise be shown a sign-in form it can never get past. With,
   * because "Bytt kode" is how a tablet moves from one farm to a whole area,
   * and that is a POST with the cookie still attached -- the receiver rescopes
   * this device in place. Signing out first would mint a second device and
   * strand a month of counts on the first, which is precisely what "all
   * activity is attributable to the device" exists to prevent. */
  if (path === '/koble') {
    return (
      <Koble
        onEnrolled={(next) => {
          setMe(next)
          invalidate()
          navigate('/')
        }}
      />
    )
  }

  /* Before the session check, like the reset pages and for the same reason:
   * the manager tapping this is doing it from a mail app on a phone that has
   * almost certainly never signed in to the dashboard. Requiring a sign-in
   * first would turn one tap into a password, on a phone, in a boat -- and the
   * whole reason the notification carries a link is that the alternative is
   * nobody approving anything until they get back to a desk.
   *
   * The token in the fragment is the authorisation, exactly as it is for a
   * password reset: it arrived at an address we already believe belongs to a
   * company manager. */
  if (path === '/godkjenn') return <Godkjenn />

  /* Where the QR on page 1 of a printed Lusedagbok lands when the phone does
   * not have the app -- with it, the phone opens the app instead. Before the
   * session check: whoever scans a book on the walkway has almost never
   * signed in to the portal on that phone, and the page's whole job is to
   * get the app installed. */
  const bok = /^\/l\/([A-Za-z0-9]{1,16})$/.exec(path)
  if (bok) return <Bokkode code={bok[1]!} />

  if (!me) return <Login onSignedIn={setMe} />

  /* A device is a signed-in actor, so it reaches this point exactly as a
   * person does -- and now, once it is admitted and named, carries on into the
   * same product a person gets.
   *
   * IT DID NOT USED TO. Every device went to <Enhet />, and the reason given
   * was true at the time: a device could reach three endpoints on a farm, and
   * the farm page would have rendered an error box for everything else --
   * which teaches somebody in wet gloves that the product is broken at the
   * moment they are deciding whether to bother with it. What changed is the
   * receiver, not this file. The portal's reads now name device-aware
   * dependencies one route at a time, so the pages resolve for a device
   * exactly as far as its membership reaches and no further.
   *
   * The capability ceiling did NOT move to make that true, and that is the
   * part worth keeping straight: DEVICE_CAPABILITIES is still {lice_count,
   * journal}. Reads were never gated on a capability -- scope and the farm's
   * purchase decide those -- so a tablet reads its farm's pages and can still
   * only write the two things it could always write. Listen is absent from the
   * rail on its own, because CUSTOMER_NAV gates that entry on `can.listen`.
   *
   * WHICH LEAVES TWO STATES THE PORTAL HAS NO SENTENCE FOR. A device waiting
   * for approval holds no membership at all, so Home would draw "Your farms"
   * with nothing under it -- indistinguishable from a company that owns none.
   * A device nobody has named yet has no identity to attribute a count to.
   * Both go to <Enhet />, which is the screen that is *about* being a device
   * and is the only one that can say either thing. Read from `me.device`,
   * which the receiver sends only for a device, rather than inferred from an
   * empty `orgs` -- that inference is true today and is exactly the kind that
   * starts admitting the wrong thing silently. */
  if (me.via === 'device' && (!me.device?.approved || me.device.needs_name))
    return <Enhet />

  return (
    <Routes
      me={me}
      onSignedOut={() => {
        setMe(null)
        invalidate()
        navigate('/')
      }}
    />
  )
}

function Routes({ me, onSignedOut }: { me: Me; onSignedOut: () => void }) {
  const path = usePath()

  let crumbs: Crumb[] = [{ label: 'Hydrophones', to: '/' }]
  let body = <NotFound />

  let m: Record<string, string> | null

  /* Undefined means a receiver that predates customers, so treat it as staff.
     These guards are cosmetic -- the receiver refuses every one of these to a
     customer anyway -- but a bookmarked URL should land on "no such page"
     rather than on a screen of error boxes. */
  const staff = me.is_staff !== false
  // A preview is not staff, but must still be able to get out.
  const canAdmin = staff || !!me.viewing_as
  // An admitted, named device reaching the customer routes. `is_staff` is
  // already false for it -- is_internal is an allowlist over credential planes
  // and the device plane is not on it -- so every staff branch below is
  // closed to it without a second guard.
  const device = me.via === 'device'

  if (path === '/') {
    // Same URL, different product. Staff land on the fleet because their
    // question is "is my hardware working"; a farmer's is "what is happening
    // at my site", and the hydrophone is a detail inside that answer.
    body = staff ? <Fleet me={me} /> : <Home me={me} />
  } else if (staff && (path === '/add' || path === '/commission' || path === '/recorders/new')) {
    // The two old paths still land here. They were the entry points for the
    // separate node and recorder forms, and someone has them bookmarked or
    // written down; 404ing a URL that worked last week to advertise a
    // reorganisation is not worth the two lines it saves.
    crumbs = [{ label: 'Hydrophones', to: '/' }, { label: 'Add' }]
    body = <Add />
  } else if (path === '/lytt' || path === '/lus' || path === '/logg'
             || path === '/foring' || path === '/laksvel'
             || path === '/loggbok' || path === '/appetitt'
             || path === '/lusedagbok') {
    /* One product, across whichever farms this customer has.
     *
     * A hop rather than a page: both products are per farm and most customers
     * have one, so with a single farm this lands straight on it. It exists
     * because the rail had nothing in it for a customer and listening was
     * reachable only from the bottom of the lice page. */
    const kind = path === '/lytt' ? 'lytt' : path === '/logg' ? 'logg'
      : path === '/loggbok' ? 'loggbok' : path === '/appetitt' ? 'appetitt'
      : path === '/lusedagbok' ? 'lusedagbok'
      : path === '/foring' ? 'foring' : path === '/laksvel' ? 'laksvel' : 'lus'
    crumbs = [{ label: kind === 'lytt' ? 'Listen' : kind === 'logg' ? 'Log'
      : kind === 'loggbok' ? 'Loggbok' : kind === 'appetitt' ? 'Appetite'
      : kind === 'lusedagbok' ? 'Lusedagbok'
      : kind === 'foring' ? 'Feeding' : kind === 'laksvel' ? 'Laksvel'
      : 'Lice' }]
    body = <Product kind={kind} />
  } else if (path === '/sites') {
    /* "Places" is our word for every site in the fleet, most of them somebody
     * else's. A customer's version of this list is their own farms, and that is
     * the front page -- so send them there rather than showing them the staff
     * table or, worse, a 404 on a URL that plainly exists.
     *
     * This is the one staff path a customer can plausibly arrive at: it is the
     * shortest guessable noun in the product, and it is what the browser
     * completes to from any `/sites/{id}` page they have already visited. */
    if (staff) {
      crumbs = [{ label: 'Sites' }]
      body = <Sites me={me} />
    } else {
      body = <Redirect to="/" />
    }
  } else if ((m = match('/sites/:id', path))) {
    crumbs = staff
      ? [{ label: 'Sites', to: '/sites' }, { label: m.id! }]
      : [{ label: 'Your farms', to: '/' }, { label: m.id! }]
    body = staff ? <SitePage id={m.id!} me={me} /> : <Farm id={m.id!} me={me} />
  } else if ((m = match('/sites/:id/lus', path)) && staff) {
    /* The customer's own lice page, opened by staff on their own session.
     *
     * Without this, nobody at Phoniq can look at the thing we ship. Staff get
     * SitePage — the editor — and the customer page renders for customers only,
     * so on a system with no customer accounts the whole farm view was
     * unreachable by anyone, and shipping a change to it was an act of faith.
     *
     * "View as this customer" does not cover this: a preview returns every
     * capability false on purpose, so the count button is correctly absent
     * there. That is right for checking what a customer SEES and useless for
     * checking what they can DO. This is the staff member as themselves —
     * their own name on the audit row if they enter anything. */
    crumbs = [{ label: 'Sites', to: '/sites' },
              { label: m.id!, to: `/sites/${encodeURIComponent(m.id!)}` },
              { label: 'Lice' }]
    body = <Farm id={m.id!} me={me} />
  } else if ((m = match('/sites/:id/map', path))) {
    /* The map, the whole screen.
     *
     * A general route: any site this reader may see, staff or customer, and
     * the receiver decides the rest. It exists as an ADDRESS because of where
     * it gets used — standing on the wall in the fôrflåte all day, sent in a
     * message as "look at merd 3", reloaded on a walkway when the signal comes
     * back. None of those survive a state that lives in one tab's memory.
     *
     * `/map` and not `/kart`: every other route in this file is English and a
     * URL is not the place to start mixing. */
    crumbs = [{ label: staff ? 'Sites' : 'Your farms', to: staff ? '/sites' : '/' },
              { label: m.id!, to: `/sites/${encodeURIComponent(m.id!)}` },
              { label: 'Map' }]
    body = <Kart id={m.id!} me={me} />
  } else if ((m = match('/sites/:id/oppsett', path))
             && (canAdmin || me.can?.layout_edit === true)) {
    /* Drawing the farm: which rings it has and where they float.
     *
     * Staff only, and that is the receiver's rule rather than this router's --
     * PUT /v1/sites/{id}/layout answers 403 to a customer whatever this file
     * does. The guard here only decides whether a bookmarked URL lands on "no
     * such page" or on a map with a save button that cannot save.
     *
     * Its own address rather than a panel on SitePage, because it is a
     * full-height map: a farm's array is three hundred metres across and
     * squeezing it into a card between two tables makes it unreadable at
     * exactly the zoom somebody places rings at. */
    /* And since the Lusedagbok, a farm's own managers: the book prints the
     * ring numbers, so the farm must be able to set them (layout_edit). */
    crumbs = staff
      ? [{ label: 'Sites', to: '/sites' },
         { label: m.id!, to: `/sites/${encodeURIComponent(m.id!)}` },
         { label: 'Set up' }]
      : [{ label: 'Your farms', to: '/' },
         { label: 'Lusedagbok', to: `/sites/${encodeURIComponent(m.id!)}/lusedagbok` },
         { label: 'Set up' }]
    body = <Oppsett id={m.id!} />
  } else if ((m = match('/sites/:id/lus_v0', path))) {
    /* The A/B arm, retired. It was the lice page WITHOUT the decision panel,
     * and the two addresses existed so the panel could be compared against its
     * own absence. The panel is gone -- the week-by-week table says what it said
     * -- so both arms now render the same page and the second address has
     * nothing left to mean.
     *
     * A redirect rather than a deletion, because this URL was handed out. It
     * goes to wherever the reader's lice page actually lives, which is not the
     * same place for the two audiences: staff have theirs at /lus, and a
     * customer's farm page IS the lice page. */
    body = <Redirect to={staff
      ? `/sites/${encodeURIComponent(m.id!)}/lus`
      : `/sites/${encodeURIComponent(m.id!)}`} />
  } else if ((m = match('/sites/:id/laksvel', path))) {
    crumbs = staff
      ? [{ label: 'Sites', to: '/sites' },
         { label: m.id!, to: `/sites/${encodeURIComponent(m.id!)}` },
         { label: 'Laksvel' }]
      : [{ label: 'Your farms', to: '/' }, { label: 'Laksvel' }]
    body = <SiteLaksvel id={m.id!} />
  } else if ((m = match('/sites/:id/apetitt', path))) {
    // Stavemåten som ble sagt høyt først. Siden heter appetitt, som i Laksvel.
    body = <Redirect to={`/sites/${encodeURIComponent(m.id!)}/appetitt`} />
  } else if ((m = match('/sites/:id/appetitt', path))) {
    crumbs = staff
      ? [{ label: 'Sites', to: '/sites' },
         { label: m.id!, to: `/sites/${encodeURIComponent(m.id!)}` },
         { label: 'Appetite' }]
      : [{ label: 'Your farms', to: '/' }, { label: 'Appetite' }]
    body = <SiteAppetitt id={m.id!} />
  } else if ((m = match('/sites/:id/foring', path))) {
    crumbs = staff
      ? [{ label: 'Sites', to: '/sites' },
         { label: m.id!, to: `/sites/${encodeURIComponent(m.id!)}` },
         { label: 'Feeding' }]
      : [{ label: 'Your farms', to: '/' }, { label: 'Feeding' }]
    body = <SiteFeeding id={m.id!} />
  } else if ((m = match('/sites/:id/lusedagbok', path))) {
    /* Lusedagboka: anleggets trykte lusebok, laget og bestilt herfra. Egen
     * adresse fordi den er en flyt med egne steg, og fordi «Bestill igjen» er
     * en lenke noen sender til innkjøperen. */
    crumbs = staff
      ? [{ label: 'Sites', to: '/sites' },
         { label: m.id!, to: `/sites/${encodeURIComponent(m.id!)}` },
         { label: 'Lusedagbok' }]
      : [{ label: 'Your farms', to: '/' }, { label: 'Lusedagbok' }]
    body = <Lusedagbok id={m.id!} me={me} />
  } else if ((m = match('/sites/:id/loggbok/spor', path))) {
    /* Ett besøk, hele skjermen. FØRST i kjeden av loggbok-rutene, av samme
     * grunn som uka står før dagen: mest spesifikke først er vanen som
     * overlever at noen gjør `match` mer tolerant senere.
     *
     * Egen adresse og ikke en utfolding i kortet -- `Kart.tsx` skrev
     * argumentet: et anlegg er tre hundre meter på skrå, og i et kort blir
     * ringene på størrelse med en fingernegl. Adressen overlever dessuten en
     * omlasting på gangbanen og lar seg sende i en melding. */
    crumbs = staff
      ? [{ label: 'Sites', to: '/sites' },
         { label: m.id!, to: `/sites/${encodeURIComponent(m.id!)}` },
         { label: 'Loggbok' }]
      : [{ label: 'Your farms', to: '/' }, { label: 'Loggbok' }]
    body = <SiteLoggbokSpor id={m.id!} />
  } else if ((m = match('/sites/:id/loggbok/uke', path))) {
    /* Ukesoppsummeringen. FØR /loggbok i kjeden, fordi kjeden er
     * første-treff -- og selv om `match` sammenlikner antall segmenter og de to
     * derfor ikke kolliderer i dag, er «mest spesifikke først» vanen som
     * overlever at noen gjør `match` mer tolerant senere. */
    crumbs = staff
      ? [{ label: 'Sites', to: '/sites' },
         { label: m.id!, to: `/sites/${encodeURIComponent(m.id!)}` },
         { label: 'Loggbok' }]
      : [{ label: 'Your farms', to: '/' }, { label: 'Loggbok' }]
    body = <SiteLoggbokUke id={m.id!} />
  } else if ((m = match('/sites/:id/loggbok', path))) {
    /* Loggboka: den daglige runden.
     *
     * En søsken av /logg og ikke en erstatning. Den akustiske loggen svarer på
     * «hva ble hørt her» og eies av analysen; denne svarer på «hva gjorde vi
     * med det» og eies av mennesket på gangbanen. To spørsmål, to sider. */
    crumbs = staff
      ? [{ label: 'Sites', to: '/sites' },
         { label: m.id!, to: `/sites/${encodeURIComponent(m.id!)}` },
         { label: 'Loggbok' }]
      : [{ label: 'Your farms', to: '/' }, { label: 'Loggbok' }]
    body = <SiteLoggbok id={m.id!} />
  } else if ((m = match('/sites/:id/logg', path))) {
    /* The log: what was heard at this farm, hour by hour.
     *
     * A sibling of /lytt rather than a replacement for /sites/:id, which is
     * already two different pages depending on who is reading -- the staff site
     * editor and the customer's lice page. Displacing either would trade one
     * working screen for another.
     *
     * Site-addressed for the same reason listening is: corroboration across an
     * array is the whole point of having six hydrophones, and it does not exist
     * at the level of one box. */
    crumbs = staff
      ? [{ label: 'Sites', to: '/sites' },
         { label: m.id!, to: `/sites/${encodeURIComponent(m.id!)}` },
         { label: 'Log' }]
      : [{ label: 'Your farms', to: '/' }, { label: 'Log' }]
    body = <SiteLog id={m.id!} />
  } else if ((m = match('/sites/:id/lytt', path))) {
    /* Listening, with a home of its own at last.
     *
     * Addressed by SITE, not by mission, and that is the fix rather than a
     * preference. A farm can have two hydrophones in the water at once -- the
     * exclusion constraints are per box and per sensor, never per place -- and
     * every existing route into audio went through `active_mission_id`, which
     * is one value picked by `ORDER BY started_at DESC LIMIT 1`. On the only
     * two-sensor farm in the fleet that resolves to the box holding no archive
     * at all, so the customer's one door to their own audio opened on the empty
     * one. A site-addressed page has both, and lets a person choose.
     *
     * Open to staff too, on their own session: the customer product should not
     * be something nobody at Phoniq can look at. */
    crumbs = staff
      ? [{ label: 'Sites', to: '/sites' },
         { label: m.id!, to: `/sites/${encodeURIComponent(m.id!)}` },
         { label: 'Listen' }]
      /* Not the site id. `site-ljsy-n-14435` is a database key printed at the
         top of the customer's own page, and a raw identifier is the loudest
         possible statement that this screen was built for us rather than for
         them. The farm's name is already the heading directly below. */
      : [{ label: 'Your farms', to: '/' }, { label: 'Listen' }]
    body = <Listen siteId={m.id!} me={me} />
  } else if (path === '/calibration' && staff) {
    crumbs = [{ label: 'Calibration' }]
    body = <Hydrophones />
  } else if (staff && (m = match('/calibration/:id', path))) {
    crumbs = [{ label: 'Calibration', to: '/calibration' }, { label: m.id! }]
    body = <HydrophonePage id={m.id!} me={me} />
  } else if (staff && (m = match('/nodes/:id', path))) {
    crumbs = [{ label: 'Hydrophones', to: '/' }, { label: m.id! }]
    body = <NodePage id={m.id!} me={me} />
  } else if ((m = match('/missions/:id', path))) {
    crumbs = [{ label: 'Hydrophones', to: '/' }, { label: m.id! }]
    body = <MissionPage id={m.id!} tab="overview" me={me} />
  } else if ((m = match('/missions/:id/:tab', path))) {
    const tab = m.tab!
    if (tab === 'levels' || tab === 'listen' || tab === 'recordings' || tab === 'activity') {
      crumbs = [
        { label: 'Hydrophones', to: '/' },
        { label: m.id!, to: `/missions/${encodeURIComponent(m.id!)}` },
        { label: tab[0]!.toUpperCase() + tab.slice(1) },
      ]
      body = <MissionPage id={m.id!} tab={tab} me={me} />
    }
  } else if (path === '/impact' && staff) {
    crumbs = [{ label: 'Ocean impact' }]
    body = <ImpactPage />
  } else if (path === '/activity' && staff) {
    crumbs = [{ label: 'Activity' }]
    body = <Activity />
  } else if (path === '/security') {
    crumbs = [{ label: 'Security' }]
    body = <SecurityDoc />
  } else if (path === '/enhet' && device) {
    /* The device's own screen, kept reachable now that a device no longer
       lands on it. Without a route it would be unreachable for a working
       tablet -- and with it goes the only way to rename this device and the
       only way to disconnect one, which is the verb somebody needs when a
       phone is being handed on or sold. */
    crumbs = [{ label: 'This device' }]
    body = <Enhet />
  } else if (path === '/account' && !device) {
    /* Not for a device. Account reads /v1/auth/sessions, which lives on the
       person plane and answers a device 401 -- so the page would render as a
       screen of error boxes. A device has no account by construction; "no such
       page" is the honest answer, and /enhet is the screen that does exist. */
    crumbs = [{ label: 'Account' }]
    body = <Account me={me} />
  } else if (path === '/enheter' && me.can?.manage_people) {
    /* Gated on the capability rather than on staff-or-not: this is a
       CUSTOMER's screen. A company manager connects the crew's phones; nobody
       at Phoniq does it for them, and staff previewing a customer get every
       capability false on purpose, so they correctly do not see it either. */
    crumbs = [{ label: 'Devices' }]
    body = <Enheter me={me} />
  } else if (path === '/operators' && canAdmin) {
    crumbs = [{ label: 'Operators' }]
    body = <Operators me={me} />
  } else if ((m = match('/operators/:id', path)) && canAdmin) {
    crumbs = [{ label: 'Operators', to: '/operators' }, { label: m.id! }]
    body = <OperatorPage id={m.id!} me={me} />
  }

  return (
    <Shell me={me} crumbs={crumbs} onSignedOut={onSignedOut}>
      {body}
    </Shell>
  )
}

/** A dead end with a door in it.
 *
 *  This page used to state the problem and stop, which leaves somebody who
 *  followed a stale link with the browser's Back button and nothing else. The
 *  front page is the right place to put them: it is their farms if they are a
 *  customer and the listening posts if they are staff, so the same link is
 *  correct for both without asking who is reading. */
function NotFound() {
  return (
    <Empty title="No such page">
      The link may be out of date, or the thing it pointed at may have been
      renamed.{' '}
      <Link to="/">Go to the front page</Link>.
    </Empty>
  )
}
